import { Component, OnInit, ViewChild, ViewChildren, QueryList, ChangeDetectorRef, ElementRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { CalculationService } from 'src/app/services/calculation.service';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ApexChart, ChartComponent } from 'ng-apexcharts';
import { HeatMapChartOptions, PieChartOptions, ColumnChartOptions, BubbleChartOptions, LineChartOptions, Statistic, Allocation, PortfolioBreakdown, Category, CategoryBreakdown, Asset, AssetBreakdown } from '../../models/interfaces'
import { ALLOCATION, PORTFOLIO_BREAKDOWN, STATISTIC_DATA, TEN_YEAR_DATA} from '../../models/data'
@Component({
  selector: 'app-print',
  templateUrl: './print.component.html',
  styleUrls: ['./print.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class PrintComponent implements OnInit {

  @ViewChild("heatMapChart") heatMapChart: ChartComponent;
  public heatMapChartOptions: Partial<HeatMapChartOptions>;

  @ViewChild("pieChart") pieChart: ChartComponent;
  public pieChartOptions: Partial<PieChartOptions>;

  @ViewChild("columnChart") columnChart: ChartComponent;
  public columnChartOptions: Partial<ColumnChartOptions>;

  @ViewChild("bubbleChart") bubbleChart: ChartComponent;
  public bubbleChartOptions: Partial<BubbleChartOptions>;

  @ViewChild("rorChart") rorChart: ChartComponent;
  public rorChartOptions: Partial<HeatMapChartOptions>;

  @ViewChild("tenYearChart") tenYearChart: ChartComponent;
  public tenYearChartOptions: Partial<LineChartOptions>;


  statisticDataSource: MatTableDataSource<Statistic> = new MatTableDataSource<Statistic>(STATISTIC_DATA);
  statisticDisplayedColumns: string[] = ['metric', 'value'];

  tenYearDataSource: MatTableDataSource<any> = new MatTableDataSource<any>(TEN_YEAR_DATA);
  tenYearDisplayedColumns: string[] = ['year', 'value'];

  breakdownDataSource: MatTableDataSource<CategoryBreakdown>;
  breakdownData: CategoryBreakdown[] = [];
  portfolioBreakdown: PortfolioBreakdown;

  categoryBreakdownColumnsToDisplay = ['category_name', 'category_ticker', 'category_rate_of_return', 'category_standard_deviation', 'category_correlation', 'category_value', 'category_percentage'];
  assetBreakdownColumnsToDisplay = ['asset_name', 'asset_ticker', 'asset_rate_of_return', 'asset_standard_deviation', 'asset_correlation', 'asset_value', 'asset_percentage'];
  @ViewChild('breakdownOuterSort', { static: true }) breakdownSort: MatSort;
  @ViewChildren('breakdownInnerSort') breakdownInnerSort: QueryList<MatSort>;
  @ViewChildren('breakdownInnerTables') breakdownInnerTables: QueryList<MatTable<AssetBreakdown>>;

  expandedCategoryBreakdown: CategoryBreakdown | null;

  constructor(private currencyPipe: CurrencyPipe, private percentPipe: PercentPipe, public calculationService: CalculationService) { 
    this.heatMapChartOptions = {
      series: [
        {
          name: "Tax-Free Cash",
          data: this.generateData(7, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Taxable Cash",
          data: this.generateData(6, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Corporate Bonds",
          data: this.generateData(5, {
            min: 0,
            max: 100
          })
        },
        {
          name: "High Yield Bonds",
          data: this.generateData(4, {
            min: 0,
            max: 100
          })
        },
        {
          name: "US Treasury (1-3 Years)",
          data: this.generateData(3, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Mid Cap Growth",
          data: this.generateData(2, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Large Cap Growth",
          data: this.generateData(1, {
            min: 0,
            max: 100
          })
        },
      ],
      chart: {
        height: 400,
        width: 400,
        type: 'heatmap',
        fontFamily: '"Open Sans Regular", sans-serif',
        animations: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: true,
        dropShadow: {
          enabled: true,
          left: 0.5,
          top: 0.5,
          opacity: 0.5
        },
      },
      plotOptions: {
        heatmap: {
          reverseNegativeShade: true,
          colorScale: {
            // inverse: true,
            ranges: [
              {
                from: -1,
                to: 0,
                name: 'Negative',
                color: '#d32f2f',
              },
              {
                from: 0,
                to: 1,
                name: 'Positive',
                color: '#244768'
              },
            ]
          }
        }
      },
      colors: [
        // '#388e3c', // Mat Green 700
        // '#d32f2f', // Mat Red 700
        // '#fecd5c', // Chapwood Yellow
        '#244768', // Chapwood Blue Primary 
      ],
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '28px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600
        },
      },
      xaxis: {
        labels: {
          style: {
            fontSize: '10px',
          },
          trim: false,
        },
        type: "category",
        categories: [
          "Tax-Free Cash",
          "Taxable Cash",
          "Corporate Bonds",
          "High Yield Bonds",
          "US Treasury (1-3 Years)",
          "Small Cap Growth",
          "Mid Cap Growth",
          "Large Cap Growth",
        ],
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
          },
        },
      },
    };
  
    this.pieChartOptions = {
      series: [44, 55, 13, 43, 22],
      chart: {
        // height: 600,
        width: 550,
        type: "pie",
        fontFamily: '"Open Sans Regular", sans-serif',
        animations: {
          enabled: false
        }
      },
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '28px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600,
        },
      },
      colors: [
        '#388e3c', // Mat Green 700
        '#d32f2f', // Mat Red 700
        '#fecd5c', // Chapwood Yellow
        '#244768', // Chapwood Blue Primary
        '#4C2A85', // Violet
        '#C37632', // Orange Copper
        '#EE33FF', // Pink Fuschia
        '#22BF88', // Ocean Green
        '#86809D', // Rhythm Grey
        '#542F26', // Brown
      ],
      labels: ["Tax-Free Cash", "Corporate Bonds", "US Treasury (1-3 Years)", "Small Cap Growth", "Large Cap Growth"],
      legend: {
        fontSize: '16px'
      },
      dataLabels: {
        style: {
          fontSize: '16px'
        }
      },
      tooltip: {
        style: {
          fontSize: '16px'
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: "bottom"
            }
          }
        }
      ]
    };
  
    this.columnChartOptions = {
      series: [
        {
          name: "Net Profit",
          data: [44, 55, 57, 56, 61, 58, 63, 60, 66]
        },
        {
          name: "Revenue",
          data: [76, 85, 101, 98, 87, 105, 91, 114, 94]
        },
        {
          name: "Free Cash Flow",
          data: [35, 41, 36, 26, 45, 48, 52, 53, 41]
        }
      ],
      chart: {
        height: 600,
        width: 600,
        type: "bar",
        fontFamily: '"Open Sans Regular", sans-serif',
        animations: {
          enabled: false
        }
      },
      grid: {
        row: {
          colors: ["#efefef", "transparent"], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      colors: [
        // '#388e3c', // Mat Green 700
        // '#d32f2f', // Mat Red 700
        // '#fecd5c', // Chapwood Yellow
        '#244768', // Chapwood Blue Primary 
      ],
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '28px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          endingShape: "rounded"
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"]
      },
      xaxis: {
        categories: [
          "<-5%",
          "-5% to -4%",
          "-4% to -3%",
          "-3% to -2%",
          "-2% to -1%",
          "-1% to 0%",
          "0% to 1%",
          "1% to 2%",
          "2% to 3%",
          "3% to 4%",
          "4% to 5%",
          ">5%",
        ]
      },
      yaxis: {
        title: {
          text: "Frequency",
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
      }
    };
    let test: any = [10, 10, 20]
    this.bubbleChartOptions = {
  
      series: [
        {
          name: "S&P 500",
          data: [
            test
          ],
        },
      ],
      chart: {
        height: 600,
        width: 600,
        type: 'scatter',
        animations: {
          enabled: false
        },
        toolbar: {
          tools: {
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            reset: false,
            pan: false
          }
        }
      },
      grid: {
        row: {
          colors: ["#efefef", "transparent"], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      dataLabels: {
        enabled: false
      },
      fill: {
        opacity: 0.8
      },
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '28px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600
        },
      },
      colors: [
        '#388e3c', // Mat Green 700
        // '#d32f2f', // Mat Red 700
        // '#fecd5c', // Chapwood Yellow
        '#244768', // Chapwood Blue Primary 
      ],
      xaxis: {
        min: 0,
        tickAmount: 5,
        title: {
          text: 'Standard Deviation',
          style: {
            fontSize: '16px',
            fontFamily: '"Open Sans Regular", sans-serif',
          },
        },
      },
      yaxis: {
        min: 0,
        tickAmount: 5,
        title: {
          text: 'Rate of Return',
          style: {
            fontSize: '16px',
            fontFamily: '"Open Sans Regular", sans-serif',
          },
        },
      },
      plotOptions: {
        bubble: {
          minBubbleRadius: 20,
          maxBubbleRadius: 20,
        }
      }
    };
  
    this.rorChartOptions = {
      series: [
        {
          name: "Tax-Free Cash",
          data: this.generateData(7, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Taxable Cash",
          data: this.generateData(6, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Corporate Bonds",
          data: this.generateData(5, {
            min: 0,
            max: 100
          })
        },
        {
          name: "High Yield Bonds",
          data: this.generateData(4, {
            min: 0,
            max: 100
          })
        },
        {
          name: "US Treasury (1-3 Years)",
          data: this.generateData(3, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Mid Cap Growth",
          data: this.generateData(2, {
            min: 0,
            max: 100
          })
        },
        {
          name: "Large Cap Growth",
          data: this.generateData(1, {
            min: 0,
            max: 100
          })
        },
      ],
      chart: {
        height: 600,
        width: 600,
        // width: '100%',
        type: 'heatmap',
        fontFamily: '"Open Sans Regular", sans-serif',
        animations: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: true,
        dropShadow: {
          enabled: true,
          left: 0.5,
          top: 0.5,
          opacity: 0.5
        },
      },
      plotOptions: {
        heatmap: {
          reverseNegativeShade: true,
          colorScale: {
            // inverse: true,
            ranges: [
              {
                from: -5,
                to: 0,
                name: 'Negative',
                color: '#d32f2f',
              },
              {
                from: 0,
                to: 5,
                name: 'Positive',
                color: '#244768'
              },
            ]
          }
        }
      },
      // colors: [
      //   // '#388e3c', // Mat Green 700
      //   // '#d32f2f', // Mat Red 700
      //   // '#fecd5c', // Chapwood Yellow
      //   '#244768', // Chapwood Blue Primary 
      // ],
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '28px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600
        },
      },
      // colors: [
      //   "#F3B415",
      //   "#F27036",
      //   "#663F59",
      //   "#6A6E94",
      //   "#4E88B4",
      //   "#00A7C6",
      //   "#18D8D8",
      //   "#A9D794",
      //   "#46AF78",
      //   "#A93F55",
      //   "#8C5E58",
      //   "#2176FF",
      //   "#33A1FD",
      //   "#7A918D",
      //   "#BAFF29"
      // ],
      xaxis: {
        labels: {
          style: {
            fontSize: '14px',
          },
          trim: false,
        },
        type: "category",
        categories: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
          "YTD"
        ],
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '10px',
          },
        },
      },
    };
  
    this.tenYearChartOptions = {
      series: [
        {
          name: "Value",
          data: [1500000, 1650000, 1670000, 1840000, 2100000, 2530000, 2600000, 2800000, 2930000, 3050000]
        }
      ],
      chart: {
        height: 600,
        width: 800,
        // width: '100%',
        type: "line",
        zoom: {
          enabled: false
        },
        fontFamily: '"Open Sans Regular", sans-serif',
        animations: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 5,
        curve: "smooth"
      },
      title: {
        text: '',
        align: 'left',
        style: {
          fontSize: '1px',
          fontFamily: '"Open Sans Regular", sans-serif',
          fontWeight: 600
        },
      },
      colors: [
        '#244768', // Chapwood Blue Primary 
      ],
      legend: {
        fontSize: '14px',
        tooltipHoverFormatter: function (val, opts) {
          return (
            val +
            " - <strong>" +
            currencyPipe.transform(opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex], 'USD', 'symbol-narrow', '1.0-0') +
            "</strong>"
          );
        },
      },
      markers: {
        size: 0,
        hover: {
          sizeOffset: 6
        }
      },
      grid: {
        row: {
          colors: ["#efefef", "transparent"], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      xaxis: {
        labels: {
          style: {
            fontSize: '14px',
          },
          trim: false,
        },
        categories: [
          '2011',
          '2012',
          '2013',
          '2014',
          '2015',
          '2016',
          '2017',
          '2018',
          '2019',
          '2020',
        ]
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '14px',
          },
          formatter: function (value) {
            if (value && value !== NaN) {
              return currencyPipe.transform(value.toString(), 'USD', 'symbol-narrow', '1.0-0');
            }
          }
        }
      },
      tooltip: {
        style: {
          fontSize: '14px',
        },
        y: {
          formatter: function (value) {
            if (value && value !== NaN) {
              return currencyPipe.transform(value.toString(), 'USD', 'symbol-narrow', '1.0-0');
            }
          }
        }
      },
      responsive: [{
        breakpoint: 768,
        options: {
          title: {
            text: '10 Year Return',
            align: 'left',
            style: {
              fontSize: '16px',
            },
          },
          chart: {
            toolbar: {
              show: false
            }
          }
        }
      }]
    };
  }
  ngOnInit(): void {
    this.initializeBreakdownTable();
  }

  generateData(count, yrange) {
    var i = 0;
    var series = [];
    while (i < count) {
      var y =
        Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;

      series.push(y);
      i++;
    }
    return series;
  }

  initializeBreakdownTable() {
    this.portfolioBreakdown = PORTFOLIO_BREAKDOWN;
    PORTFOLIO_BREAKDOWN.categoryBreakdowns.forEach(categoryBreakdown => {
      if (categoryBreakdown.assetBreakdowns && Array.isArray(categoryBreakdown.assetBreakdowns) && categoryBreakdown.assetBreakdowns.length) {
        this.breakdownData = [...this.breakdownData, { ...categoryBreakdown, assetBreakdowns: new MatTableDataSource(categoryBreakdown.assetBreakdowns) }];
      } else {
        this.breakdownData = [...this.breakdownData, categoryBreakdown];
      }
    })

    this.breakdownDataSource = new MatTableDataSource(this.breakdownData);
    this.breakdownDataSource.sort = this.breakdownSort;
  }

}
